// Kleiner Toast-Speicher des Pakets „Extras" (Erinnerungen, Export fertig, Fehler).
// Gerendert von <ExtrasToaster/> (Teil von `extraGlobals`, siehe index.tsx).
import { create } from 'zustand';

export interface Toast {
  id: string;
  kind: 'info' | 'success' | 'error' | 'reminder';
  title: string;
  body?: string;
  /** Optionaler Link (z. B. zur Nachricht). */
  href?: string;
  /** Optionale Aktion (z. B. „Erledigt"). */
  action?: { label: string; run: () => void };
  /** ms bis zum Ausblenden; 0 = bleibt bis geschlossen. */
  timeout: number;
}

interface ToastState {
  toasts: Toast[];
  /** Anzahl eingehängter <ExtrasToaster/> im App-Baum (über `extraGlobals`). */
  hosts: number;
  push(t: Omit<Toast, 'id' | 'timeout'> & { id?: string; timeout?: number }): string;
  dismiss(id: string): void;
  clear(): void;
}

let seq = 0;

/** Höchstzahl gleichzeitig sichtbarer flüchtiger Toasts; bleibende (timeout 0, z. B. Erinnerungen) zählen nicht mit. */
export const MAX_TRANSIENT_TOASTS = 5;

/** Bleibende Toasts nie verdrängen, flüchtige auf die letzten MAX_TRANSIENT_TOASTS kürzen. */
function trim(list: Toast[]): Toast[] {
  const transient = list.filter((t) => t.timeout > 0);
  const drop = new Set(transient.slice(0, Math.max(0, transient.length - MAX_TRANSIENT_TOASTS)).map((t) => t.id));
  return drop.size ? list.filter((t) => !drop.has(t.id)) : list;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  hosts: 0,
  push: (t) => {
    const id = t.id ?? `t${++seq}`;
    const toast: Toast = { timeout: t.kind === 'reminder' ? 0 : 6000, ...t, id };
    set((s) => ({ toasts: trim([...s.toasts.filter((x) => x.id !== id), toast]) }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

type Fallback = () => void;
let fallback: Fallback | null = null;

/**
 * Wird aufgerufen, wenn ein Toast kommt, aber kein <ExtrasToaster/> im App-Baum hängt (Shell hat `extraGlobals`
 * nicht eingebunden). ui.tsx registriert hier einen eigenständigen Toaster, damit Toasts nie verloren gehen.
 */
export function setToastFallback(fn: Fallback | null): void {
  fallback = fn;
}

export const toast = (t: Parameters<ToastState['push']>[0]): string => {
  const id = useToastStore.getState().push(t);
  if (useToastStore.getState().hosts === 0) fallback?.();
  return id;
};
